<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import { usePipelinesStore } from 'dashboard/stores/pipelines';

import DealsListLayout from 'dashboard/components-next/Deals/DealsListLayout.vue';
import DealEmptyState from 'dashboard/components-next/Deals/EmptyState/DealEmptyState.vue';
import DealsFilterDialog from 'dashboard/components-next/Deals/DealsFilterDialog.vue';
import DealCard from 'dashboard/components-next/Deals/DealCard/DealCard.vue';
import DealsBulkActionBar from '../components/DealsBulkActionBar.vue';
import ChangeDealStageDialog from '../components/ChangeDealStageDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';

const DEBOUNCE_DELAY = 300;

const dealsStore = useDealsStore();
const pipelinesStore = usePipelinesStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const filterDialogRef = ref(null);
const bulkDeleteDialogRef = ref(null);
const changeStageDialogRef = ref(null);

const deals = computed(() => dealsStore.getDealsList);
const meta = computed(() => dealsStore.meta);
const isFetchingList = computed(() => dealsStore.uiFlags.fetchingList);

// --- Bulk selection state ---
const selectedDealIds = ref([]);
const isSelecting = ref(false);
const isBulkActionLoading = ref(false);
const selectedCount = computed(() => selectedDealIds.value.length);
const hasSelection = computed(
  () => isSelecting.value || selectedCount.value > 0
);

const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('DEALS_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('DEALS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('DEALS_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('DEALS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('DEALS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('DEALS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);

const visibleDealIds = computed(() => deals.value.map(d => d.id));

const clearSelection = () => {
  selectedDealIds.value = [];
  isSelecting.value = false;
};

const startSelection = () => {
  isSelecting.value = true;
};

const openBulkDeleteDialog = () => {
  if (!selectedDealIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const openChangeStageDialog = () => {
  if (!selectedDealIds.value.length || isBulkActionLoading.value) return;
  changeStageDialogRef.value?.open?.();
};

const toggleSelectAll = shouldSelect => {
  const currentSelection = new Set(selectedDealIds.value);
  if (shouldSelect) {
    visibleDealIds.value.forEach(id => currentSelection.add(id));
  } else {
    visibleDealIds.value.forEach(id => currentSelection.delete(id));
  }
  selectedDealIds.value = Array.from(currentSelection);
};

const toggleDealSelection = ({ id, value }) => {
  const isAlreadySelected = selectedDealIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedDealIds.value = [...selectedDealIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedDealIds.value = selectedDealIds.value.filter(
      dealId => dealId !== id
    );
  }
};

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
    changeStageDialogRef.value?.close?.();
  }
});

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const activeFilters = ref({ assignedToId: null, stageIds: [] });
const hasActiveFilters = computed(
  () =>
    !!activeFilters.value.assignedToId ||
    activeFilters.value.stageIds.length > 0
);

const showEmptyStateLayout = computed(
  () =>
    !isFetchingList.value &&
    deals.value.length === 0 &&
    !searchValue.value &&
    !hasActiveFilters.value
);

const updateURLParams = (page, search = '') => {
  const query = { ...route.query, page: page.toString() };
  if (search) query.search = search;
  else delete query.search;
  router.replace({ query });
};

const fetchDeals = async (page, search) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  if (page !== undefined || search !== undefined) {
    updateURLParams(currentPage, currentSearch);
  }
  await dealsStore.get({
    page: currentPage,
    search: currentSearch || undefined,
  });
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchDeals(1, query);
}, DEBOUNCE_DELAY);

const onPageChange = page => fetchDeals(page, searchValue.value);

// Nova negociacao: formulario na coluna esquerda da pagina de criacao
const openCreate = () => {
  router.push({
    name: 'deals_new',
    params: { accountId: route.params.accountId },
  });
};

const openFilterDialog = () => filterDialogRef.value?.open();

const navigateToManageFields = () => {
  router.push({
    name: 'attributes_list',
    params: { accountId: route.params.accountId },
    query: { tab: 'deal_attribute' },
  });
};

const goToPipelineSettings = () => {
  router.push({
    name: 'pipelines_settings_index',
    params: { accountId: route.params.accountId },
  });
};

const applyFilters = filters => {
  activeFilters.value = { ...filters };
};

const isUpdating = computed(() => dealsStore.uiFlags.updatingItem);
const isDeleting = computed(() => dealsStore.uiFlags.deletingItem);

const goToDealDetail = dealId => {
  router.push({
    name: 'deals_edit',
    params: { accountId: route.params.accountId, dealId },
  });
};

const handleUpdateDeal = () => {
  useAlert(t('DEALS.DETAILS.MESSAGES.UPDATED'));
};

const handleDeleteDeal = async id => {
  try {
    await dealsStore.delete(id);
    useAlert(t('DEALS.LIST.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message ?? t('DEALS.LIST.DELETE.ERROR_MESSAGE'));
  }
};

// --- Bulk actions ---
const assignLabels = async labels => {
  if (!labels.length || !selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      labels: { add: labels },
    });
    useAlert(t('DEALS_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await fetchDeals();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const removeLabels = async labels => {
  if (!labels.length || !selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      labels: { remove: labels },
    });
    useAlert(t('DEALS_BULK_ACTIONS.REMOVE_LABELS_SUCCESS'));
    clearSelection();
    await fetchDeals();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.REMOVE_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteDeals = async () => {
  if (!selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      action_name: 'delete',
    });
    useAlert(t('DEALS_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await fetchDeals();
    bulkDeleteDialogRef.value?.close?.();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const changeStage = async pipelineStageId => {
  if (!selectedDealIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Deal',
      ids: selectedDealIds.value,
      pipeline_stage_id: pipelineStageId,
    });
    useAlert(t('DEALS_BULK_ACTIONS.CHANGE_STAGE_SUCCESS'));
    clearSelection();
    await fetchDeals();
    changeStageDialogRef.value?.close?.();
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.CHANGE_STAGE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const exportSelectedDeals = async () => {
  try {
    await dealsStore.export(selectedDealIds.value);
    useAlert(t('DEALS_BULK_ACTIONS.EXPORT_SUCCESS'));
  } catch {
    useAlert(t('DEALS_BULK_ACTIONS.EXPORT_FAILED'));
  }
};

onMounted(async () => {
  searchValue.value = searchQuery.value;
  await pipelinesStore.get();
  fetchDeals();
});
</script>

<template>
  <DealsListLayout
    :search-value="searchValue"
    :header-title="t('DEALS.ALL_DEALS.TITLE')"
    :current-page="pageNumber"
    :total-items="Number(meta?.totalCount || 0)"
    :show-pagination-footer="!!deals.length && !hasSelection"
    :has-active-filters="hasActiveFilters"
    view-mode="list"
    :show-view-toggle="false"
    @search="onSearch"
    @update:current-page="onPageChange"
    @create="openCreate"
    @manage-pipeline="goToPipelineSettings"
    @filter="openFilterDialog"
    @manage-fields="navigateToManageFields"
    @select="startSelection"
    @refresh="() => fetchDeals()"
  >
    <DealEmptyState
      v-if="showEmptyStateLayout"
      :title="t('DEALS.EMPTY_STATE.TITLE')"
      :subtitle="t('DEALS.EMPTY_STATE.SUBTITLE')"
      :button-label="t('DEALS.EMPTY_STATE.BUTTON_LABEL')"
      @create="openCreate"
    />
    <div
      v-else-if="isFetchingList"
      class="flex items-center justify-center p-8"
    >
      <span class="text-n-slate-11 text-base">{{ t('DEALS.LOADING') }}</span>
    </div>
    <template v-else>
      <DealsBulkActionBar
        v-if="hasSelection"
        :visible-deal-ids="visibleDealIds"
        :selected-deal-ids="selectedDealIds"
        :is-loading="isBulkActionLoading"
        @toggle-all="toggleSelectAll"
        @cancel-selection="clearSelection"
        @assign-labels="assignLabels"
        @remove-labels="removeLabels"
        @export-selected="exportSelectedDeals"
        @delete-selected="openBulkDeleteDialog"
        @change-stage="openChangeStageDialog"
      />
      <div class="flex flex-col gap-2" :class="hasSelection ? 'pt-2' : ''">
        <div
          class="flex items-center gap-4 px-4 py-2 text-xs font-medium uppercase tracking-wide text-n-slate-10"
        >
          <span class="flex-1 min-w-0">{{ t('DEALS.LIST.COLUMNS.NAME') }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.CONTACT')
          }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.COMPANY')
          }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.STAGE')
          }}</span>
          <span class="flex-1 min-w-0">{{
            t('DEALS.LIST.COLUMNS.RESPONSIBLE')
          }}</span>
          <span class="w-28 text-right shrink-0">{{
            t('DEALS.LIST.COLUMNS.VALUE')
          }}</span>
          <div class="size-7 shrink-0" />
        </div>
        <DealCard
          v-for="deal in deals"
          :key="deal.id"
          :deal="deal"
          :is-updating="isUpdating"
          :is-deleting="isDeleting"
          :selectable="hasSelection"
          :is-selected="selectedDealIds.includes(deal.id)"
          disable-expand
          @name-click="goToDealDetail(deal.id)"
          @update-deal="handleUpdateDeal"
          @delete-deal="handleDeleteDeal"
          @select="value => toggleDealSelection({ id: deal.id, value })"
        />
      </div>
      <Dialog
        v-if="selectedCount"
        ref="bulkDeleteDialogRef"
        type="alert"
        :title="bulkDeleteDialogTitle"
        :description="bulkDeleteDialogDescription"
        :confirm-button-label="bulkDeleteDialogConfirmLabel"
        :is-loading="isBulkActionLoading"
        @confirm="deleteDeals"
      />
      <ChangeDealStageDialog
        v-if="selectedCount"
        ref="changeStageDialogRef"
        :is-loading="isBulkActionLoading"
        @confirm="changeStage"
      />
    </template>

    <DealsFilterDialog
      ref="filterDialogRef"
      :active-filters="activeFilters"
      @apply="applyFilters"
    />
  </DealsListLayout>
</template>
